/**
 * Phase 17 Plan 17-02 — API client for the 7 KPI endpoints under
 * `/api/workorders/:woId/kpis`.
 *
 * Pattern mirrors services/work-orders.ts (Phase 16-03):
 *  - Each function throws `ApiError` on non-2xx.
 *  - Returns the typed response object on success.
 */

import { api } from '@/lib/api/axios';
import { toApiError } from '@/lib/api/error';
import type {
  CreateKpiBody,
  DeactivateKpiResponse,
  KpiDetail,
  KpiListItem,
  UpdateKpiBody,
} from '@/types/kpi';

/** `GET /api/workorders/:woId/kpis?includeInactive=<bool>` */
export async function getKpisForWorkOrderRequest(
  woId: number,
  includeInactive: boolean,
): Promise<KpiListItem[]> {
  try {
    const params = new URLSearchParams();
    params.set('includeInactive', String(includeInactive));
    const { data } = await api.get<KpiListItem[]>(
      `/api/workorders/${String(woId)}/kpis?${params.toString()}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `GET /api/workorders/:woId/kpis/:id` */
export async function getKpiDetailRequest(woId: number, kpiId: number): Promise<KpiDetail> {
  try {
    const { data } = await api.get<KpiDetail>(
      `/api/workorders/${String(woId)}/kpis/${String(kpiId)}`,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/workorders/:woId/kpis` — create KPI, returns 201 + full detail. */
export async function createKpiRequest(woId: number, body: CreateKpiBody): Promise<KpiDetail> {
  try {
    const { data } = await api.post<KpiDetail>(`/api/workorders/${String(woId)}/kpis`, body);
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `PUT /api/workorders/:woId/kpis/:id` — edit KPI (409 if unit changed with entries). */
export async function updateKpiRequest(
  woId: number,
  kpiId: number,
  body: UpdateKpiBody,
): Promise<KpiDetail> {
  try {
    const { data } = await api.put<KpiDetail>(
      `/api/workorders/${String(woId)}/kpis/${String(kpiId)}`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/workorders/:woId/kpis/:id/deactivate` — soft-delete. */
export async function deactivateKpiRequest(
  woId: number,
  kpiId: number,
): Promise<DeactivateKpiResponse> {
  try {
    const { data } = await api.post<DeactivateKpiResponse>(
      `/api/workorders/${String(woId)}/kpis/${String(kpiId)}/deactivate`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/workorders/:woId/kpis/:id/reactivate` — set IsActive=true. */
export async function reactivateKpiRequest(woId: number, kpiId: number): Promise<KpiDetail> {
  try {
    const { data } = await api.post<KpiDetail>(
      `/api/workorders/${String(woId)}/kpis/${String(kpiId)}/reactivate`,
      null,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/** `POST /api/workorders/:woId/kpis/from-template/:templateId` — copy-to-WO (one-way clone). */
export async function createKpiFromTemplateRequest(
  woId: number,
  templateId: number,
  body: { readonly overrideMachineIds?: readonly number[] },
): Promise<KpiDetail> {
  try {
    const { data } = await api.post<KpiDetail>(
      `/api/workorders/${String(woId)}/kpis/from-template/${String(templateId)}`,
      body,
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}

/**
 * Phase 23 (Plan 23-03) D-239 — `POST /api/workorders/:woId/kpis/:id/pin`
 * with body `{ pinned: bool }`. Toggles the dashboard pin flag on a single
 * KPI. Server-side gated by `kpis.edit`; max 3 pinned per WO (returns 400
 * with title containing "tooManyPins" when the cap is exceeded).
 *
 * Note: route is nested under WorkOrderKpisController per D-239 correction
 * — there is no flat `/api/kpis/...` controller in this codebase.
 */
export async function togglePinKpiRequest(
  woId: number,
  kpiId: number,
  pinned: boolean,
): Promise<KpiDetail> {
  try {
    const { data } = await api.post<KpiDetail>(
      `/api/workorders/${String(woId)}/kpis/${String(kpiId)}/pin`,
      { pinned },
    );
    return data;
  } catch (e) {
    throw toApiError(e);
  }
}
